import dataToolSvc from "@services/dataToolSvc";
import { getShopCategoriesForKeys, getShopCategoriesForKeysV2 } from '@services/vueShopCategoriesSvc';

const state = () => ({
  categoryData: null,
  productTypeConfig: null,
  categoryFilters: null,
  categoryFilterValues: {},
  productFilters: null,
  products: null,
});

const getters = {
  filteredProducts(state) {
    return getFilteredProducts(state.products, state.productFilters);
  },

  categoryFiltersAsMapByKey(state) {
    if (!state.categoryFilters) {
      return {};
    }
    const filtersMap = {};
    for (const filter of state.categoryFilters) {
      filtersMap[filter.urlParam] = filter;
    }
    return filtersMap;
  }
};

const actions = {
  async loadCategoryData({ commit }, { keys, useV2 = false }) {
    const fetchAndCommit = async (keysToQuery) => {
      const { data } = await (useV2 ? getShopCategoriesForKeysV2 : getShopCategoriesForKeys)({ keys: keysToQuery });
      commit('setCategoryData', data);
      commit('setProductsFromCategoryData');
      return data;
    };

    try {
      return await fetchAndCommit(keys.join(','));
    } catch (err) {
      const { code, categoryKeys, uniqueGroups } = err.response?.data ?? {};

      switch (code) {
        case 'NO_CATEGORIES_FOUND':
          return await fetchAndCommit('permanent-hair-color');
        case 'CATEGORIES_MISSING':
          return await fetchAndCommit(categoryKeys);
        case 'DUPLICATED_GROUPS':
          return await fetchAndCommit(uniqueGroups);
        default:
          throw err;
      }
    }
  },

  loadProductTypeConfig({ commit }) {
    return new Promise((resolve) => {
      dataToolSvc.getData({ mixinKey: "shop_categories_product_type_config" }).then(res => {
        commit('setProductTypeConfig', res.data.productTypes);
        resolve();
      });
    });
  },

  loadFilters({ commit }) {
    return new Promise((resolve) => {
      dataToolSvc.getData({ mixinKey: 'category_filters' }).then(res => {
        commit('setCategoryFilters', res?.data?.filters);
        resolve();
      });
    });
  },

  doInitFilters({ commit, state }, { routeQuery }) {
    const categoryFilters = JSON.parse(JSON.stringify(state.categoryFilters));
    categoryFilters.forEach(filter => {
      filter.disabled = false;
      filter.options.forEach(option => {
        option.disabled = false;
      });

      if (routeQuery[filter.urlParam]) {
        const urlValues = routeQuery[filter.urlParam].split(',');

        const validUrlValues = new Set();

        urlValues.forEach(value => {
          const exist = filter.options.find(option => option.value === value);
          if (exist) {
            validUrlValues.add(value);
          }
        });

        if (validUrlValues.size > 0) {
          commit('setCategoryFilterValue', { key: filter.urlParam, value: [...validUrlValues] });
        }
      }
    });

    commit('setCategoryFilters', categoryFilters);
  },
};

const mutations = {
  setCategoryData(state, val) {
    state.categoryData = val;
  },

  setProductsFromCategoryData(state) {
    state.products = state?.categoryData?.categoryGroups.reduce((acc, group) => {
      const groupProds = group.productsByProductType?.reduce((acc, type) => {
        acc.push(...type.products);
        return acc;
      }, []);

      acc.push(...groupProds);
      return acc;
    }, []);
  },

  setProductTypeConfig(state, val) {
    state.productTypeConfig = val;
  },

  setCategoryFilters(state, val) {
    state.categoryFilters = val;
  },

  setCategoryFilterValues(state, val) {
    state.categoryFilterValues = val;
  },

  setCategoryFilterValue(state, val) {
    const { key, value } = val;
    state.categoryFilterValues = { ...state.categoryFilterValues, [key]: value };
  },

  setProductFilters(state, val) {
    state.productFilters = val;
  },

  setProducts(state, val) {
    state.products = val;
  },

  updateDisabledStates(state) {
    state.categoryFilters.forEach(categoryFilter => {
      let allOptionsDisabled = true;

      categoryFilter.options.forEach(option => {
        let productFiltersCopy = { ...state.productFilters };

        option.data.forEach(productFilter => {
          productFiltersCopy[productFilter.field] = productFilter.values;
        });

        let newFilteredProducts = getFilteredProducts(state.products, productFiltersCopy);

        if (!newFilteredProducts || !newFilteredProducts.length) {
          option.disabled = true;
        } else {
          option.disabled = false;
          allOptionsDisabled = false;
        }
      });

      categoryFilter.disabled = allOptionsDisabled;
    });
  }
};

function getFilteredProducts(products, filters) {
  if (!products || !filters || !Object.keys(filters).length) {
    return products;
  }

  let filtered = products.filter(product => {
    for (let key in filters) {
      if (product[key] == undefined || !filters[key].includes(product[key])) {
        return false;
      }
    }
    return true;
  });

  return filtered;
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
